import { useCallback, useEffect, useRef, useState } from "react";
import { cancelPRFAuthentication, checkPRFSupport } from "@/lib/webauthn/prf";
import { readPRFCatalog } from "@/lib/webauthn/prf-server-persistence";
import { unlockPRFBackup } from "@/lib/webauthn/prf-setup";

function readiness() {
	if (typeof window === "undefined") {
		return { bound: false, sealed: false };
	}
	const catalog = readPRFCatalog();
	const bound = Boolean(catalog?.credentials.length);
	const sealed = Boolean(
		catalog?.vault &&
			catalog.credentials.some(
				(row) => row.keyId === catalog.vault?.keyId && row.encryptedBackup
			)
	);
	return { bound, sealed };
}
export function usePRF() {
	const [isSupported, setIsSupported] = useState(false);
	const [isWorking, setIsWorking] = useState(false);
	const [error, setError] = useState<string | null>(null);
	const [state, setState] = useState(readiness);
	const attempt = useRef(0);
	const cancelUnlock = useCallback(() => {
		attempt.current++;
		cancelPRFAuthentication();
		setIsWorking(false);
		setError(null);
	}, []);
	useEffect(() => {
		const refresh = () => setState(readiness());
		window.addEventListener("prf-credential-hydrated", refresh);
		window.addEventListener("storage", refresh);
		checkPRFSupport().then(setIsSupported);
		return () => {
			attempt.current++;
			cancelPRFAuthentication();
			window.removeEventListener("prf-credential-hydrated", refresh);
			window.removeEventListener("storage", refresh);
		};
	}, []);
	const unlock = useCallback(async (): Promise<string | null> => {
		const current = ++attempt.current;
		setIsWorking(true);
		setError(null);
		try {
			const plaintext = await unlockPRFBackup();
			return current === attempt.current ? plaintext : null;
		} catch (err) {
			if (current === attempt.current) {
				setError(
					err instanceof Error
						? err.message
						: "Use your backup password or try another passkey."
				);
			}
			return null;
		} finally {
			if (current === attempt.current) {
				setIsWorking(false);
			}
		}
	}, []);
	const clearError = useCallback(() => setError(null), []);
	return {
		isSupported,
		isWorking,
		error,
		hasPRFCredential: state.bound,
		hasSealedBackup: state.sealed,
		unlock,
		cancelUnlock,
		clearError,
	};
}
